import { defineStore } from "pinia"
import { apiV1 } from "@/config/axios"

export const useFileManagerStore = defineStore("fileManager", {
  state: () => ({
    items: [],
    currentFolderId: null,
    expandedFolders: new Set(),
    selectedItems: [],
    breadcrumbs: [],
    isLoading: false,
    draggedItem: null,
    searchQuery: "",
    sortBy: null,
    sortOrder: null,
    viewMode: "table",
    selectedColumns: ["name", "size", "updated_at", "modified_by", "actions"],
    allItems: [],
    userPreferencesData: null,
    folderTreeData: null,
    pagination: {
      current_page: 1,
      per_page: 25,
      total: 0,
      from: 0,
      to: 0
    }
  }),

  getters: {
    currentItems: (state) => {
      return state.items
    },

    folderTree: (state) => {
      if (state.folderTreeData && state.folderTreeData.children) {
        const transformApiTree = (apiNode) => {
          return {
            id: apiNode.id,
            name: apiNode.name,
            type: "folder",
            path: apiNode.path,
            documents_count: apiNode.documents_count,
            has_children: apiNode.children && apiNode.children.length > 0,
            children_count: apiNode.children ? apiNode.children.length : 0,
            expanded: state.expandedFolders.has(apiNode.id),
            children: apiNode.children
              ? apiNode.children.map(transformApiTree)
              : []
          }
        }

        return state.folderTreeData.children.map(transformApiTree)
      }

      const buildTree = (parentId = null) => {
        return state.allItems
          .filter(
            (item) => item.parentFolderId === parentId && item.type === "folder"
          )
          .map((folder) => ({
            ...folder,
            children: buildTree(folder.id),
            expanded: state.expandedFolders.has(folder.id)
          }))
      }
      return buildTree()
    },

    selectedItemsCount: (state) => state.selectedItems.length,

    totalSize: (state) => {
      return state.selectedItems.reduce((total, item) => {
        return total + (item.size || 0)
      }, 0)
    },

    hasPagination: (state) => state.pagination.total > 25,
    totalPages: (state) =>
      Math.ceil(state.pagination.total / state.pagination.per_page)
  },

  actions: {
    async fetchItems(folderId = null) {
      try {
        this.isLoading = true

        const params = new URLSearchParams()

        if (folderId !== null) {
          params.append("folderId", folderId)
        }

        if (this.searchQuery) {
          params.append("searchQuery", this.searchQuery)
        }

        if (this.sortBy && this.sortOrder) {
          params.append("sort_by", `${this.sortBy}:${this.sortOrder}`)
        }

        params.append("page", this.pagination.current_page)
        params.append("per_page", this.pagination.per_page)

        const queryString = params.toString()
        const url = `/file-manager${queryString ? `?${queryString}` : ""}`

        const response = await apiV1.get(url)

        const data = response.data.data

        const itemsData = data.documents.data || {}
        const itemsArray = Array.isArray(itemsData)
          ? itemsData
          : Object.values(itemsData)
        const allItems = itemsArray.map((item) => {
          if (item.type === "folder") {
            return {
              id: item.id,
              name: item.name,
              type: "folder",
              parentFolderId: folderId,
              size: item.size,
              description: item.description,
              created_at: item.created_at,
              updated_at: item.updated_at,
              created_by: item.created_by,
              has_children: item.has_children,
              children_count: item.children_count,
              documents_count: item.documents_count,
              document_ids: item.document_ids || [],
              permissions: ["read", "write", "delete"]
            }
          } else {
            return {
              id: item.id,
              name: item.title,
              title: item.title,
              type: "file",
              parentFolderId: folderId,
              size: item.size,
              mimeType: item.mime_type,
              extension: item.file_extension?.toLowerCase(),
              created_at: item.created_at || new Date().toISOString(),
              updated_at: item.updated_at || new Date().toISOString(),
              created_by: item.created_by,
              modified_by: item.modified_by,
              updater_name: item.updater_name,
              contractor: item.contractor,
              file_url: item.file_url,
              status: item.status,
              document_type_id: item.document_type_id,
              document_type_name: item.document_type_name,
              content: item.content,
              permissions: ["read", "write", "delete"]
            }
          }
        })

        this.items = [...allItems]
        this.currentFolderId = folderId

        if (data.documents) {
          this.pagination = {
            current_page: data.documents.current_page || 1,
            per_page: data.documents.per_page || 25,
            total: data.documents.total || 0,
            from: data.documents.from || 0,
            to: data.documents.to || 0
          }
        }

        if (data.breadcrumb) {
          this.breadcrumbs = data.breadcrumb.map((item) => ({
            id: item.id,
            name: item.name,
            icon: item.id === null ? "pi pi-home" : "pi pi-folder"
          }))
        } else {
          this.breadcrumbs = [{ id: null, name: "Main", icon: "pi pi-home" }]
        }

        this.allItems = [
          ...this.allItems.filter((item) => item.parentFolderId !== folderId),
          ...this.items
        ]
      } finally {
        this.isLoading = false
      }
    },

    async createFolder(name, parentFolderId = null) {
      const targetFolderId =
        parentFolderId !== null ? parentFolderId : this.currentFolderId

      const response = await apiV1.post("/file-manager/folders", {
        name: name,
        folder_id: targetFolderId
      })

      const newFolder = response.data

      this.items.push(newFolder)
      this.allItems.push(newFolder)

      this.expandedFolders.add(newFolder.id)
      if (newFolder.parentFolderId) {
        this.expandedFolders.add(newFolder.parentFolderId)
      }

      return newFolder
    },

    async deleteItems(itemIds) {
      const deletePromises = itemIds.map(async (itemId) => {
        const stringId = String(itemId)
        const numberId = Number(itemId)

        let item = this.items.find(
          (item) =>
            item &&
            (item.id === itemId ||
              item.id === stringId ||
              item.id === numberId)
        )

        if (!item) {
          item = this.allItems.find(
            (item) =>
              item &&
              (item.id === itemId ||
                item.id === stringId ||
                item.id === numberId)
          )
        }

        if (!item) {
          item = this.selectedItems.find(
            (selectedItem) =>
              selectedItem &&
              (selectedItem.id === itemId ||
                selectedItem.id === stringId ||
                selectedItem.id === numberId)
          )
        }

        if (!item) {
          throw new Error(`Item with ID ${itemId} not found`)
        }

        const itemType = item.type === "folder" ? "folder" : "document"

        return await apiV1.delete(`/file-manager/${itemId}`, {
          data: { type: itemType }
        })
      })

      await Promise.all(deletePromises)

      this.items = this.items.filter((item) => !itemIds.includes(item.id))
      this.allItems = this.allItems.filter(
        (item) => !itemIds.includes(item.id)
      )

      this.selectedItems = this.selectedItems.filter(
        (item) => !itemIds.includes(item.id)
      )
    },

    async moveItems(itemIds, targetFolderId) {
      const documentIds = []
      const folderIds = []

      itemIds.forEach((id) => {
        const stringId = String(id)
        const numberId = Number(id)

        let item = this.allItems.find(
          (item) =>
            item.id === id || item.id === stringId || item.id === numberId
        )

        if (!item) {
          item = this.items.find(
            (item) =>
              item.id === id || item.id === stringId || item.id === numberId
          )
        }

        if (item) {
          if (item.type === "file") {
            documentIds.push(item.id)
          } else if (item.type === "folder") {
            folderIds.push(item.id)
          }
        }
      })

      const payload = {}

      if (documentIds.length > 0) {
        payload.document_ids = documentIds
      }

      if (folderIds.length > 0) {
        payload.folder_ids = folderIds
      }

      payload.folder_id = targetFolderId

      if (documentIds.length === 0 && folderIds.length === 0) {
        throw new Error("No items to move")
      }

      await apiV1.post("/file-manager/move", payload)

      itemIds.forEach((id) => {
        const stringId = String(id)
        const numberId = Number(id)

        const item = this.items.find(
          (item) =>
            item &&
            (item.id === id || item.id === stringId || item.id === numberId)
        )
        const allItem = this.allItems.find(
          (item) =>
            item &&
            (item.id === id || item.id === stringId || item.id === numberId)
        )

        if (item) {
          item.parentFolderId = targetFolderId
          item.updated_at = new Date().toISOString()
        }

        if (allItem) {
          allItem.parentFolderId = targetFolderId
          allItem.updated_at = new Date().toISOString()
        }
      })
    },

    isChildOf(targetId, sourceId) {
      if (!targetId) return false

      const findParentChain = (id) => {
        const item = this.allItems.find((item) => item.id === id)
        if (!item) return []
        if (item.parentFolderId === sourceId) return [id]
        if (item.parentFolderId)
          return [id, ...findParentChain(item.parentFolderId)]
        return [id]
      }

      const chain = findParentChain(targetId)
      return chain.includes(sourceId)
    },

    async renameItem(itemId, newName) {
      const stringId = String(itemId)
      const numberId = Number(itemId)

      const item = this.allItems.find(
        (item) =>
          item &&
          (item.id === itemId || item.id === stringId || item.id === numberId)
      )

      if (!item) {
        throw new Error("Item not found")
      }

      const itemType = item.type === "folder" ? "folder" : "document"

      await apiV1.put(`/file-manager/${itemId}/rename`, {
        type: itemType,
        name: newName
      })

      const currentItem = this.items.find(
        (item) =>
          item &&
          (item.id === itemId || item.id === stringId || item.id === numberId)
      )
      const allItem = this.allItems.find(
        (item) =>
          item &&
          (item.id === itemId || item.id === stringId || item.id === numberId)
      )

      if (currentItem) {
        currentItem.name = newName
        currentItem.updated_at = new Date().toISOString()
      }

      if (allItem) {
        allItem.name = newName
        allItem.updated_at = new Date().toISOString()
      }
    },

    async uploadFiles(files, targetFolderId = null) {
      const formData = new FormData()
      files.forEach((file) => {
        formData.append("files", file)
      })
      formData.append(
        "folder_id",
        targetFolderId || this.currentFolderId || ""
      )

      const { data } = await apiV1.post("/file-manager/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data"
        }
      })

      const newFiles = data.files.map((file) => ({
        id: file.id,
        name: file.name,
        type: "file",
        parentFolderId: targetFolderId || this.currentFolderId,
        size: file.size,
        mimeType: file.mime_type,
        extension: file.extension,
        created_at: file.created_at,
        updated_at: file.updated_at,
        created_by: file.created_by,
        file_url: file.file_url,
        permissions: file.permissions || ["read", "write", "delete"]
      }))

      this.items.push(...newFiles)
      this.allItems.push(...newFiles)
      return newFiles
    },

    async openFolder(folderId) {
      this.resetPagination()
      await this.fetchItems(folderId)
    },

    toggleFolderExpansion(folderId) {
      if (this.expandedFolders.has(folderId)) {
        this.expandedFolders.delete(folderId)
      } else {
        this.expandedFolders.add(folderId)
      }
    },

    expandFolder(folderId) {
      this.expandedFolders.add(folderId)
    },

    collapseFolder(folderId) {
      this.expandedFolders.delete(folderId)
    },

    navigateToParent() {
      if (this.breadcrumbs.length > 1) {
        const parentFolder = this.breadcrumbs[this.breadcrumbs.length - 2]
        this.openFolder(parentFolder.id)
      } else {
        this.openFolder(null)
      }
    },

    navigateToBreadcrumb(index) {
      if (index === 0) {
        this.openFolder(null)
      } else {
        const targetFolder = this.breadcrumbs[index]
        this.openFolder(targetFolder.id)
      }
    },

    selectItem(item) {
      const index = this.selectedItems.findIndex(
        (selected) => selected.id === item.id
      )
      if (index === -1) {
        this.selectedItems.push(item)
      } else {
        this.selectedItems.splice(index, 1)
      }
    },

    selectAllItems() {
      this.selectedItems = [...this.currentItems]
    },

    clearSelection() {
      this.selectedItems = []
    },

    setDraggedItem(item) {
      this.draggedItem = item
    },

    clearDraggedItem() {
      this.draggedItem = null
    },

    async setSearchQuery(query) {
      this.searchQuery = query
      this.resetPagination()
      await this.fetchItems(this.currentFolderId)
    },

    async setSorting(field, order = null) {
      if (this.sortBy === field && !order) {
        this.sortOrder = this.sortOrder === "asc" ? "desc" : "asc"
      } else {
        this.sortBy = field
        this.sortOrder = order || "asc"
      }
      this.resetPagination()
      await this.fetchItems(this.currentFolderId)
    },

    setViewMode(mode) {
      this.viewMode = mode
    },

    setSelectedColumns(columns) {
      this.selectedColumns = columns
    },

    updateColumnSettings(columnOrder, selectedColumns) {
      this.selectedColumns = selectedColumns
    },

    expandAllFolders() {
      const allFolderIds = this.allItems
        .filter((item) => item.type === "folder")
        .map((folder) => folder.id)

      allFolderIds.forEach((id) => this.expandedFolders.add(id))
    },

    async loadAllFolders() {
      try {
        const response = await apiV1.get("/file-manager/all-folders")
        const data = response.data

        if (data.folder) {
          const flattenFolders = (folderNode, parentId = null) => {
            const folders = []

            if (folderNode.id !== null) {
              folders.push({
                id: folderNode.id,
                name: folderNode.name,
                type: "folder",
                parentFolderId: parentId,
                path: folderNode.path,
                documents_count: folderNode.documents_count,
                document_ids: folderNode.document_ids || [],
                has_children:
                  folderNode.children && folderNode.children.length > 0,
                children_count: folderNode.children
                  ? folderNode.children.length
                  : 0,
                size: null,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
                created_by: null,
                permissions: ["read", "write", "delete"]
              })
            }

            if (folderNode.children && folderNode.children.length > 0) {
              folderNode.children.forEach((child) => {
                folders.push(...flattenFolders(child, folderNode.id))
              })
            }

            return folders
          }

          this.allItems = flattenFolders(data.folder)

          this.folderTreeData = data.folder
        } else {
          this.allItems = response.data || []
        }

        if (this.expandedFolders.size === 0) {
          this.expandedFolders = new Set()
        }
      } catch {}
    },

    async loadUserPreferences() {
      try {
        const response = await apiV1.get("/file-manager/user-preferences")
        this.userPreferencesData = response.data
        return response.data
      } catch {
        return null
      }
    },

    async saveUserPreferences(columnOrder, selectedColumns) {
      if (!this.userPreferencesData) {
        throw new Error("No user preferences data loaded")
      }

      const updatedPreferences = {
        ...this.userPreferencesData,
        column_order: columnOrder,
        visible_columns: selectedColumns
      }

      await apiV1.post("/file-manager/user-preferences", updatedPreferences)
      this.userPreferencesData = updatedPreferences
    },

    getDefaultSettings() {
      return this.userPreferencesData?.default_settings || null
    },

    async downloadItems(itemIds) {
      try {
        const documentIds = []
        const folderIds = []

        itemIds.forEach((id) => {
          const stringId = String(id)
          const numberId = Number(id)

          const item = this.allItems.find(
            (item) =>
              item.id === id || item.id === stringId || item.id === numberId
          )

          if (item) {
            if (item.type === "file") {
              documentIds.push(item.id)
            } else if (item.type === "folder") {
              folderIds.push(item.id)
            }
          }
        })

        const payload = {}

        if (documentIds.length > 0) {
          payload.document_ids = documentIds
        }

        if (folderIds.length > 0) {
          payload.folder_ids = folderIds
        }

        if (documentIds.length === 0 && folderIds.length === 0) {
          throw new Error("No items to download")
        }

        const response = await apiV1.post("/file-manager/download", payload)

        const { download_url, message, name } = response.data

        window.open(download_url, "_blank")

        const filename = name
        return { success: true, filename, message }
      } catch (error) {
        const errorMessage =
          error.response?.data?.message || "Failed to create ZIP archive"
        throw new Error(errorMessage)
      }
    },

    setPage(page) {
      this.pagination.current_page = page
    },

    setPerPage(perPage) {
      this.pagination.per_page = perPage
      this.pagination.current_page = 1
      this.pagination.from = 0
      this.pagination.to = 0
    },

    resetPagination() {
      this.pagination.current_page = 1
      this.pagination.per_page = 25
      this.pagination.total = 0
      this.pagination.from = 0
      this.pagination.to = 0
    }
  }
})
